import React, {
    createContext,
    useState,
    useEffect,
    ReactNode,
    useContext,
  } from "react";
  import { usePremiumCardFlag } from "../context/FeatureContext";
  
  interface AtmosRewardsData {
    [key: string]: any;
  }
  
  interface AtmosRewardsContextType {
    data: AtmosRewardsData[];
    isLoading: boolean;
    error: string | null;
  }
  
  const AtmosRewardsContext = createContext<
    AtmosRewardsContextType | undefined
  >(undefined);
  
  interface AtmosRewardsProviderProps {
    token: string;
    children: ReactNode;
  }
  
  const AtmosRewardsProvider: React.FC<AtmosRewardsProviderProps> = ({
    token,
    children,
  }) => {
    const [data, setData] = useState<AtmosRewardsData[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const isPremiumCardEnabled = usePremiumCardFlag();

    useEffect(() => {
      if (!isPremiumCardEnabled) {
        setIsLoading(false);
        return; // Don't render anything if the premium card feature is not enabled
      }

        const fetchData = async () => {
          try {
            const response = await fetch(
              process.env.REACT_APP_PREMIUM_CARD_CONTENT_ENDPOINT as string,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Ocp-Apim-Subscription-Key": process.env.REACT_APP_MPCHOICE_BFF_KEY as string,
                },
              }
            );
      
            if (!response.ok) {
              throw new Error("Failed to fetch data");
            }
      
            const json = await response.json();
      
            setData(json?.entry?.blocks);
            setIsLoading(false);
          } catch (err: any) {
            setError(err.message || "Unknown error");
            setIsLoading(false);
          }
        };
      
        fetchData();
      }, [isPremiumCardEnabled]);
      
  
    return (
      <AtmosRewardsContext.Provider value={{ data, isLoading, error }}>
        {children}
      </AtmosRewardsContext.Provider>
    );
  };
  
  // Custom hook
  const useAtmosRewards = () => {
    const context = useContext(AtmosRewardsContext);
    if (context === undefined) {
      throw new Error(
        "useAtmosRewards must be used within an AtmosRewardsProvider"
      );
    }
    return context;  
  };
  
export { 
  AtmosRewardsProvider, 
  AtmosRewardsContext, 
  useAtmosRewards 
};
  